import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  CreditCard,
  Plus,
  Check,
  Clock,
  X,
  AlertCircle,
  Store,
  Banknote,
  FileText,
  Calendar,
  User,
  Loader2,
  Users
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PAYMENT_METHODS = [
  { value: "check", label: "Check", icon: Banknote },
  { value: "bank_transfer", label: "Bank Transfer", icon: CreditCard },
  { value: "other", label: "Other", icon: FileText }
];

export default function AdminPayouts() {
  const [payouts, setPayouts] = useState([]);
  const [earnings, setEarnings] = useState([]);
  const [distributors, setDistributors] = useState([]);
  const [distributorPayouts, setDistributorPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("resellers");

  // Create payout dialog
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [selectedStore, setSelectedStore] = useState(null);
  const [payoutAmount, setPayoutAmount] = useState("");
  const [createNotes, setCreateNotes] = useState("");

  // Mark as paid dialog
  const [markPaidDialogOpen, setMarkPaidDialogOpen] = useState(false);
  const [selectedPayout, setSelectedPayout] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState("check");
  const [checkNumber, setCheckNumber] = useState("");
  const [referenceNumber, setReferenceNumber] = useState("");
  const [paymentNotes, setPaymentNotes] = useState("");

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem("session_token")}` }
  });

  // Resellers who still have money owed. `pending_payout` already has both settled
  // payouts and unsettled (pending/processing) ones deducted by the API.
  const payableEarnings = earnings.filter(
    (e) => e.store_id !== "0" && (e.pending_payout || 0) > 0
  );
  const selectedEarning = payableEarnings.find((e) => e.store_id === selectedStore) || null;
  const maxPayout = selectedEarning?.pending_payout || 0;

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [payoutsRes, earningsRes, distRes, distPayoutsRes] = await Promise.all([
        axios.get(`${API}/admin/payouts`, getAuthHeaders()),
        axios.get(`${API}/earnings/all`, getAuthHeaders()),
        axios.get(`${API}/admin/distributors`, getAuthHeaders()),
        axios.get(`${API}/admin/distributor-payouts`, getAuthHeaders())
      ]);
      setPayouts(payoutsRes.data);
      setEarnings(earningsRes.data.reseller_earnings || []);
      setDistributors(distRes.data || []);
      setDistributorPayouts(distPayoutsRes.data || []);
    } catch (error) {
      toast.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePayout = async () => {
    if (!selectedStore || !payoutAmount) {
      toast.error("Please select a reseller and enter amount");
      return;
    }

    const amount = parseFloat(payoutAmount);
    if (!(amount > 0)) {
      toast.error("Enter a payout amount greater than zero");
      return;
    }
    // pending_payout already excludes anything committed to an unsettled payout.
    if (amount > maxPayout + 0.005) {
      toast.error(`Amount exceeds the $${maxPayout.toFixed(2)} available for this reseller`);
      return;
    }

    setSaving(true);
    try {
      await axios.post(`${API}/admin/payouts/create`, {
        store_id: selectedStore,
        amount: parseFloat(payoutAmount),
        notes: createNotes
      }, getAuthHeaders());
      toast.success("Payout created successfully");
      setCreateDialogOpen(false);
      setSelectedStore(null);
      setPayoutAmount("");
      setCreateNotes("");
      fetchData();
    } catch (error) {
      toast.error(
        error.response?.data?.message || error.response?.data?.detail || "Failed to create payout"
      );
    } finally {
      setSaving(false);
    }
  };

  const openMarkPaidDialog = (payout) => {
    setSelectedPayout({ ...payout, type: 'Funeral Home' });
    setPaymentMethod("check");
    setCheckNumber("");
    setReferenceNumber("");
    setPaymentNotes("");
    setMarkPaidDialogOpen(true);
  };

  const openDistributorMarkPaidDialog = (dist) => {
    setSelectedPayout({
      type: 'Distributor',
      distributor_id: dist.distributor_id,
      business_name: dist.name,
      subdomain: dist.email,
      amount: dist.pending_earnings,
      distributor: dist
    });
    setPaymentMethod("check");
    setCheckNumber("");
    setReferenceNumber("");
    setPaymentNotes(`Payout for ${dist.name}`);
    setMarkPaidDialogOpen(true);
  };

  const handleMarkAsPaid = async () => {
    if (!selectedPayout) return;

    if (paymentMethod === "check" && !checkNumber) {
      toast.error("Please enter the check number");
      return;
    }

    setSaving(true);
    try {
      if (selectedPayout.type === 'Distributor') {
        await axios.post(`${API}/admin/distributor-payouts`, {
          distributor_id: selectedPayout.distributor_id,
          amount: selectedPayout.amount,
          payment_method: paymentMethod,
          check_number: checkNumber || null,
          reference_number: referenceNumber || null,
          notes: paymentNotes || null
        }, getAuthHeaders());
        toast.success("Distributor payout recorded!");
      } else {
        await axios.put(`${API}/admin/payouts/${selectedPayout.payout_id}/status`, {
          status: "completed",
          payment_method: paymentMethod,
          check_number: checkNumber || null,
          reference_number: referenceNumber || null,
          notes: paymentNotes || null
        }, getAuthHeaders());
        toast.success("Payout marked as paid!");
      }

      setMarkPaidDialogOpen(false);
      setSelectedPayout(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update payout");
    } finally {
      setSaving(false);
    }
  };

  const updatePayoutStatus = async (payoutId, status) => {
    try {
      await axios.put(`${API}/admin/payouts/${payoutId}/status`, { status }, getAuthHeaders());
      toast.success(`Payout marked as ${status}`);
      fetchData();
    } catch (error) {
      toast.error("Failed to update payout status");
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-700',
      processing: 'bg-blue-100 text-blue-700',
      completed: 'bg-green-100 text-green-700',
      failed: 'bg-red-100 text-red-700'
    };
    return styles[status] || 'bg-slate-100 text-slate-700';
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return <Check className="w-3 h-3" />;
      case 'processing': return <Clock className="w-3 h-3" />;
      case 'failed': return <X className="w-3 h-3" />;
      default: return <AlertCircle className="w-3 h-3" />;
    }
  };

  // Calculate total pending payouts
  const totalPending = earnings.reduce((sum, e) => sum + (e.pending_payout || 0), 0);
  const totalPaidOut = earnings.reduce((sum, e) => sum + (e.paid_out || 0), 0);

  // Calculate distributor totals
  const totalDistPending = distributors.reduce((sum, d) => sum + (d.pending_earnings || 0), 0);
  const totalDistPaid = distributors.reduce((sum, d) => sum + Math.max(0, (d.total_earnings || 0) - (d.pending_earnings || 0)), 0);

  // Distributor payout handled via generic dialog

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-amber-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-payouts">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-slate-900">
          Payouts
        </h2>
        <p className="text-slate-500">Track and process commission payments</p>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="resellers" className="gap-2">
            <Store className="w-4 h-4" />
            Reseller Payouts
          </TabsTrigger>
          <TabsTrigger value="distributors" className="gap-2">
            <Users className="w-4 h-4" />
            Distributor Payouts
          </TabsTrigger>
        </TabsList>

        {/* Reseller Payouts Tab */}
        <TabsContent value="resellers" className="space-y-6">
          <div className="flex justify-end">
            <Button
              onClick={() => setCreateDialogOpen(true)}
              className="bg-amber-600 hover:bg-amber-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Payout
            </Button>
          </div>

          {/* Reseller Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white rounded-xl shadow-sm border p-5">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-yellow-100 rounded-xl flex items-center justify-center">
                  <Clock className="w-6 h-6 text-yellow-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Pending Payouts</p>
                  <p className="text-2xl font-bold text-slate-900">${totalPending.toFixed(2)}</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl shadow-sm border p-5">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                  <Check className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Total Paid Out</p>
                  <p className="text-2xl font-bold text-slate-900">${totalPaidOut.toFixed(2)}</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl shadow-sm border p-5">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                  <Store className="w-6 h-6 text-amber-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Active Resellers</p>
                  <p className="text-2xl font-bold text-slate-900">{earnings.length}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Pending Earnings by Reseller — table */}
          {earnings.filter(e => (e.pending_payout || 0) > 0).length > 0 && (
            <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
              <h3 className="font-semibold text-slate-900 p-5 pb-3 flex items-center gap-2">
                <Banknote className="w-5 h-5 text-amber-600" />
                Pending Earnings by Reseller
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-left">
                  <thead className="bg-slate-50/80 text-slate-500 text-xs uppercase tracking-wide border-y border-slate-100">
                    <tr>
                      <th className="px-5 py-3 font-medium">Reseller</th>
                      <th className="px-5 py-3 font-medium text-right">Pending Payout</th>
                      <th className="px-5 py-3 font-medium text-right">Total Earnings</th>
                      <th className="px-5 py-3 font-medium text-center">Orders</th>
                      <th className="px-5 py-3 font-medium text-right">Total Sales</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50">
                    {earnings.filter(e => (e.pending_payout || 0) > 0).map((earning) => (
                      <tr key={earning.store_id} className="hover:bg-slate-50/50">
                        <td className="px-5 py-3">
                          <p className="font-medium text-slate-900">{earning.business_name}</p>
                          <p className="text-xs text-slate-500">{earning.subdomain}</p>
                        </td>
                        <td className="px-5 py-3 text-right font-bold text-amber-600">${(earning.pending_payout || 0).toFixed(2)}</td>
                        <td className="px-5 py-3 text-right">${(earning.total_earnings || 0).toFixed(2)}</td>
                        <td className="px-5 py-3 text-center">{earning.order_count || 0}</td>
                        <td className="px-5 py-3 text-right">${(earning.total_sales || 0).toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Payouts Table */}
          <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="font-semibold">Reseller</TableHead>
                  <TableHead className="font-semibold">Amount</TableHead>
                  <TableHead className="font-semibold">Status</TableHead>
                  <TableHead className="font-semibold">Payment Details</TableHead>
                  <TableHead className="font-semibold">Date</TableHead>
                  <TableHead className="font-semibold text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {payouts.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-12 text-slate-500">
                      <Banknote className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                      <p>No payouts yet</p>
                      <p className="text-sm">Create one when resellers have pending earnings</p>
                    </TableCell>
                  </TableRow>
                ) : (
                  payouts.map((payout) => (
                    <TableRow key={payout.payout_id}>
                      <TableCell>
                        <div>
                          <p className="font-medium text-slate-900">{payout.business_name}</p>
                          <p className="text-xs text-slate-500">{payout.subdomain}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <p className="text-lg font-bold text-amber-600">${payout.amount.toFixed(2)}</p>
                      </TableCell>
                      <TableCell>
                        <Badge className={`${getStatusBadge(payout.status)} flex items-center gap-1 w-fit`}>
                          {getStatusIcon(payout.status)}
                          {payout.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {payout.status === 'completed' ? (
                          <div className="text-sm">
                            <p className="text-slate-700">
                              {payout.method === 'check' && payout.method_details?.check_number && (
                                <span className="flex items-center gap-1">
                                  <Banknote className="w-3 h-3" />
                                  Check #{payout.method_details.check_number}
                                </span>
                              )}
                              {payout.method === 'bank_transfer' && (
                                <span className="flex items-center gap-1">
                                  <CreditCard className="w-3 h-3" />
                                  Bank Transfer
                                </span>
                              )}
                              {(payout.reference_number || payout.method_details?.reference_number) && (
                                <span className="text-slate-500 text-xs block">Ref: {payout.reference_number || payout.method_details?.reference_number}</span>
                              )}
                            </p>
                            {payout.notes && (
                              <p className="text-slate-500 text-xs mt-1 truncate max-w-[200px]" title={payout.notes}>
                                {payout.notes}
                              </p>
                            )}
                          </div>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          <p className="text-slate-700">{formatDate(payout.created_at)}</p>
                          {payout.processed_at && (
                            <p className="text-xs text-slate-500">
                              Paid: {formatDate(payout.processed_at)}
                            </p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        {payout.status === 'pending' && (
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => updatePayoutStatus(payout.payout_id, 'processing')}
                            >
                              <Clock className="w-4 h-4 mr-1" />
                              Processing
                            </Button>
                            <Button
                              size="sm"
                              onClick={() => openMarkPaidDialog(payout)}
                              className="bg-green-600 hover:bg-green-700"
                            >
                              <Check className="w-4 h-4 mr-1" />
                              Mark Paid
                            </Button>
                          </div>
                        )}
                        {payout.status === 'processing' && (
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              size="sm"
                              onClick={() => openMarkPaidDialog(payout)}
                              className="bg-green-600 hover:bg-green-700"
                            >
                              <Check className="w-4 h-4 mr-1" />
                              Mark Paid
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => updatePayoutStatus(payout.payout_id, 'failed')}
                              className="text-red-600 border-red-200 hover:bg-red-50"
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        )}
                        {payout.status === 'completed' && (
                          <Badge variant="outline" className="text-green-600">
                            <Check className="w-3 h-3 mr-1" />
                            Paid
                          </Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        {/* Distributor Payouts Tab */}
        <TabsContent value="distributors" className="space-y-6">
          {/* Distributor Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white rounded-xl shadow-sm border p-5">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                  <Clock className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Pending Commissions</p>
                  <p className="text-2xl font-bold text-slate-900">${totalDistPending.toFixed(2)}</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl shadow-sm border p-5">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                  <Check className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Total Paid Out</p>
                  <p className="text-2xl font-bold text-slate-900">${totalDistPaid.toFixed(2)}</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl shadow-sm border p-5">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                  <Users className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Active Distributors</p>
                  <p className="text-2xl font-bold text-slate-900">{distributors.length}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Distributor Earnings Table */}
          <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
            <div className="p-4 border-b">
              <h3 className="font-semibold text-slate-900">Distributor Commission Summary</h3>
              <p className="text-sm text-slate-500">Commission is 10% of order subtotal (before shipping)</p>
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Distributor</TableHead>
                  <TableHead className="text-center">Commission %</TableHead>
                  <TableHead className="text-center">Resellers</TableHead>
                  <TableHead className="text-right">Total Earned</TableHead>
                  <TableHead className="text-right">Pending</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {distributors.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-slate-500">
                      No distributors found
                    </TableCell>
                  </TableRow>
                ) : (
                  distributors.map((dist) => (
                    <TableRow key={dist.distributor_id}>
                      <TableCell>
                        <div>
                          <p className="font-medium">{dist.name}</p>
                          <p className="text-xs text-slate-500">{dist.email}</p>
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge variant="secondary">{dist.commission_percent}%</Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        {dist.assigned_resellers_count || 0}
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        ${(dist.total_earnings || 0).toFixed(2)}
                      </TableCell>
                      <TableCell className="text-right">
                        <span className="font-medium text-amber-600">
                          ${(dist.pending_earnings || 0).toFixed(2)}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        {(dist.pending_earnings || 0) > 0 && (
                          <Button
                            size="sm"
                            onClick={() => openDistributorMarkPaidDialog(dist)}
                            disabled={saving}
                            className="bg-green-600 hover:bg-green-700"
                          >
                            <Check className="w-4 h-4 mr-1" />
                            Pay ${(dist.pending_earnings || 0).toFixed(2)}
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Distributor Payout History */}
          {distributorPayouts.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
              <div className="p-4 border-b">
                <h3 className="font-semibold text-slate-900">Payout History</h3>
              </div>
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="font-semibold">Distributor</TableHead>
                    <TableHead className="font-semibold">Amount</TableHead>
                    <TableHead className="font-semibold">Status</TableHead>
                    <TableHead className="font-semibold">Payment Details</TableHead>
                    <TableHead className="font-semibold">Date</TableHead>
                    <TableHead className="font-semibold text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {distributorPayouts.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-12 text-slate-500">
                        <Banknote className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                        <p>No payouts yet</p>
                        <p className="text-sm">Create one when distributors have pending earnings</p>
                      </TableCell>
                    </TableRow>
                  ) : (
                    distributorPayouts.map((payout, idx) => (
                      <TableRow key={idx}>
                        <TableCell>
                          <div>
                            <p className="font-medium text-slate-900">{payout.distributor_name}</p>
                            <p className="text-xs text-slate-500">{payout.distributor_email}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <p className="text-lg font-bold text-amber-600">${(payout.amount || 0).toFixed(2)}</p>
                        </TableCell>
                        <TableCell>
                          <Badge className={`${getStatusBadge(payout.status)} flex items-center gap-1 w-fit`}>
                            {getStatusIcon(payout.status)}
                            {payout.status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {payout.status === 'completed' ? (
                            <div className="text-sm">
                              <p className="text-slate-700">
                                {payout.method === 'check' && payout.method_details?.check_number && (
                                  <span className="flex items-center gap-1">
                                    <Banknote className="w-3 h-3" />
                                    Check #{payout.method_details.check_number}
                                  </span>
                                )}
                                {payout.method === 'bank_transfer' && (
                                  <span className="flex items-center gap-1">
                                    <CreditCard className="w-3 h-3" />
                                    Bank Transfer
                                  </span>
                                )}
                                {(payout.reference_number || payout.method_details?.reference_number) && (
                                  <span className="text-slate-500 text-xs block">Ref: {payout.reference_number || payout.method_details?.reference_number}</span>
                                )}
                              </p>
                              {payout.notes && (
                                <p className="text-slate-500 text-xs mt-1 truncate max-w-[200px]" title={payout.notes}>
                                  {payout.notes}
                                </p>
                              )}
                            </div>
                          ) : (
                            <span className="text-slate-400">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">
                            <p className="text-slate-700">{formatDate(payout.created_at)}</p>
                            {payout.processed_at && (
                              <p className="text-xs text-slate-500">
                                Paid: {formatDate(payout.processed_at)}
                              </p>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <span className="text-slate-400">-</span>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Create Payout Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-amber-600" />
              Create Payout
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Select Reseller</Label>
              <Select value={selectedStore || ""} onValueChange={setSelectedStore}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose a reseller" />
                </SelectTrigger>
                <SelectContent>
                  {payableEarnings.map((earning) => (
                    <SelectItem key={earning.store_id} value={earning.store_id}>
                      <div className="flex items-center justify-between w-full gap-4">
                        <span>{earning.business_name}</span>
                        <span className="text-amber-600 font-medium">
                          ${(earning.pending_payout || 0).toFixed(2)} owed
                          {(earning.reserved_payout || 0) > 0 && (
                            <span className="text-slate-400 font-normal ml-1">
                              (${earning.reserved_payout.toFixed(2)} in a pending payout)
                            </span>
                          )}
                        </span>
                      </div>
                    </SelectItem>
                  ))}
                  {payableEarnings.length === 0 && (
                    <div className="px-2 py-3 text-sm text-slate-500">
                      No reseller has an unpaid balance right now.
                    </div>
                  )}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Payout Amount ($)</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                max={selectedEarning ? maxPayout : undefined}
                value={payoutAmount}
                onChange={(e) => setPayoutAmount(e.target.value)}
                placeholder="Enter amount"
              />
              {selectedEarning && (
                <p className="text-xs text-slate-500">
                  Available to pay: ${maxPayout.toFixed(2)}
                  {(selectedEarning.reserved_payout || 0) > 0 &&
                    ` — $${selectedEarning.reserved_payout.toFixed(2)} is already committed to a pending payout`}
                </p>
              )}
              {selectedEarning && parseFloat(payoutAmount) > maxPayout + 0.005 && (
                <p className="text-xs text-red-600">
                  Amount exceeds the available balance.
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label>Notes (Optional)</Label>
              <Textarea
                value={createNotes}
                onChange={(e) => setCreateNotes(e.target.value)}
                placeholder="Any notes about this payout..."
                rows={2}
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button variant="outline" onClick={() => setCreateDialogOpen(false)} className="flex-1">
                Cancel
              </Button>
              <Button
                onClick={handleCreatePayout}
                disabled={saving}
                className="flex-1 bg-amber-600 hover:bg-amber-700"
              >
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                Create Payout
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Mark as Paid Dialog */}
      <Dialog open={markPaidDialogOpen} onOpenChange={setMarkPaidDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Check className="w-5 h-5 text-green-600" />
              Mark as Paid ({selectedPayout?.type})
            </DialogTitle>
          </DialogHeader>

          {selectedPayout && (
            <div className="space-y-4 mt-4">
              {/* Payout Summary */}
              <div className="p-4 bg-slate-50 rounded-xl">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="font-medium text-slate-900">{selectedPayout.business_name}</p>
                    <p className="text-sm text-slate-500">{selectedPayout.subdomain}</p>
                  </div>
                  <p className="text-2xl font-bold text-amber-600">${selectedPayout.amount.toFixed(2)}</p>
                </div>
              </div>

              {/* Payment Method */}
              <div className="space-y-2">
                <Label>Payment Method</Label>
                <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PAYMENT_METHODS.map((method) => (
                      <SelectItem key={method.value} value={method.value}>
                        <div className="flex items-center gap-2">
                          <method.icon className="w-4 h-4" />
                          {method.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Check Number (if check selected) */}
              {paymentMethod === "check" && (
                <div className="space-y-2">
                  <Label>Check Number *</Label>
                  <Input
                    value={checkNumber}
                    onChange={(e) => setCheckNumber(e.target.value)}
                    placeholder="e.g., 1234"
                  />
                </div>
              )}

              {/* Reference Number */}
              <div className="space-y-2">
                <Label>Reference / Confirmation Number (Optional)</Label>
                <Input
                  value={referenceNumber}
                  onChange={(e) => setReferenceNumber(e.target.value)}
                  placeholder="e.g., transaction ID, wire reference"
                />
              </div>

              {/* Notes */}
              <div className="space-y-2">
                <Label>Payment Notes</Label>
                <Textarea
                  value={paymentNotes}
                  onChange={(e) => setPaymentNotes(e.target.value)}
                  placeholder="e.g., Mailed on 01/06/2025, sent to address on file..."
                  rows={3}
                />
              </div>

              <div className="flex gap-3 pt-4 border-t">
                <Button variant="outline" onClick={() => setMarkPaidDialogOpen(false)} className="flex-1">
                  Cancel
                </Button>
                <Button
                  onClick={handleMarkAsPaid}
                  disabled={saving}
                  className="flex-1 bg-green-600 hover:bg-green-700"
                >
                  {saving ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Check className="w-4 h-4 mr-2" />
                  )}
                  Confirm Payment
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
